import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "148";
const height = "40";
const viewBox = "0 0 148 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill="#253B80"
            d="M55.09 8.18h-8.153c-.27 0-.53.098-.736.277a1.156 1.156 0 00-.383.696L42.52 30.41a.702.702 0 00.156.556.682.682 0 00.517.241h3.892c.27 0 .531-.098.736-.276.206-.179.342-.426.383-.697l.89-5.733c.041-.271.177-.518.382-.697.205-.178.466-.277.736-.277h2.581c5.37 0 8.47-2.642 9.28-7.879.364-2.29.015-4.09-1.04-5.351-1.159-1.384-3.214-2.116-5.943-2.116zm.94 7.764c-.445 2.975-2.68 2.975-4.842 2.975h-1.23l.863-5.555a.694.694 0 01.23-.418.673.673 0 01.441-.165h.564c1.473 0 2.861 0 3.579.853.428.509.56 1.265.396 2.31zm23.431-.096h-3.904a.673.673 0 00-.441.166.694.694 0 00-.23.418l-.173 1.11-.273-.403c-.845-1.247-2.73-1.664-4.611-1.664-4.315 0-8 3.323-8.717 7.983-.373 2.325.157 4.548 1.454 6.098 1.19 1.426 2.892 2.02 4.918 2.02 3.476 0 5.404-2.273 5.404-2.273l-.174 1.103a.702.702 0 00.153.557.683.683 0 00.517.243H76.9c.27 0 .532-.098.737-.276.205-.179.341-.426.383-.697l2.11-13.587a.698.698 0 00-.153-.556.679.679 0 00-.516-.242zm-5.442 7.726c-.377 2.268-2.147 3.79-4.405 3.79-1.134 0-2.04-.369-2.621-1.07-.577-.695-.797-1.686-.613-2.788.352-2.249 2.152-3.821 4.375-3.821 1.109 0 2.01.374 2.604 1.081.595.714.83 1.71.66 2.809zm26.236-7.726h-3.924a1.122 1.122 0 00-.938.506l-5.411 8.104-2.294-7.788a1.156 1.156 0 00-.41-.593 1.124 1.124 0 00-.677-.229h-3.855a.67.67 0 00-.554.289.697.697 0 00-.091.625l4.321 12.895-4.063 5.832a.698.698 0 00.201.99c.107.066.229.1.354.1h3.918a1.116 1.116 0 00.931-.494l13.05-19.152a.708.708 0 00.044-.714.686.686 0 00-.602-.37z"
            key="key-0"
        />
        <path
            fill="#179BD7"
            d="M113.244 8.18h-8.154c-.27 0-.53.099-.735.277a1.157 1.157 0 00-.383.696l-3.298 21.257a.696.696 0 00.386.734.668.668 0 00.284.063h4.185c.189 0 .371-.069.514-.194a.806.806 0 00.268-.487l.936-6.025c.041-.271.177-.518.382-.697.205-.178.466-.277.736-.277h2.58c5.372 0 8.47-2.642 9.281-7.879.366-2.29.014-4.09-1.041-5.351-1.158-1.384-3.212-2.116-5.941-2.116zm.941 7.764c-.445 2.975-2.68 2.975-4.843 2.975h-1.229l.865-5.555a.688.688 0 01.229-.418.668.668 0 01.441-.165h.564c1.471 0 2.861 0 3.578.853.428.509.558 1.265.395 2.31zm23.429-.096h-3.902a.668.668 0 00-.441.166.693.693 0 00-.229.418l-.173 1.11-.274-.403c-.845-1.247-2.729-1.664-4.61-1.664-4.314 0-7.998 3.323-8.716 7.983-.371 2.325.157 4.548 1.454 6.098 1.192 1.426 2.892 2.02 4.917 2.02 3.477 0 5.404-2.273 5.404-2.273l-.174 1.103a.705.705 0 00.155.558.682.682 0 00.518.242h3.515c.27 0 .531-.098.736-.277.205-.178.341-.425.382-.696l2.112-13.587a.703.703 0 00-.157-.556.683.683 0 00-.517-.242zm-5.442 7.726c-.374 2.268-2.147 3.79-4.405 3.79-1.131 0-2.04-.369-2.621-1.07-.577-.695-.794-1.686-.613-2.788.354-2.249 2.152-3.821 4.375-3.821 1.109 0 2.01.374 2.604 1.081.597.714.833 1.71.66 2.809zm10.045-14.81l-3.346 21.646a.684.684 0 00.154.555.683.683 0 00.516.242h3.364c.559 0 1.034-.412 1.119-.973l3.3-21.256a.685.685 0 00-.154-.556.685.685 0 00-.516-.243h-3.767a.675.675 0 00-.441.167.698.698 0 00-.229.418z"
            key="key-1"
        />
        <path
            fill="#253B80"
            d="M8.662 35.338l.624-4.027-1.39-.032H1.266L5.874 1.566a.385.385 0 01.128-.233.373.373 0 01.246-.092H17.43c3.713 0 6.275.786 7.612 2.336.627.727 1.027 1.487 1.22 2.324.203.877.206 1.926.008 3.204l-.014.094v.82l.627.36c.479.247.909.58 1.27.985.536.621.883 1.412 1.03 2.349.151.963.1 2.11-.147 3.408-.286 1.494-.749 2.794-1.373 3.858a7.89 7.89 0 01-2.176 2.425c-.83.598-1.816 1.053-2.93 1.344-1.08.286-2.312.43-3.663.43h-.87c-.622 0-1.227.228-1.701.636a2.691 2.691 0 00-.887 1.61l-.066.363-1.101 7.097-.05.26c-.013.083-.036.124-.07.152a.183.183 0 01-.114.042H8.662z"
            key="key-2"
        />
        <path
            fill="#179BD7"
            d="M27.477 9.293c-.034.217-.072.44-.115.667-1.474 7.698-6.52 10.358-12.963 10.358h-3.281c-.788 0-1.452.581-1.575 1.372L7.863 32.52l-.475 3.07a.867.867 0 00.19.688.841.841 0 00.638.3h5.82c.688 0 1.274-.51 1.382-1.2l.057-.301 1.096-7.07.07-.387a1.407 1.407 0 011.383-1.203h.87c5.638 0 10.051-2.327 11.341-9.062.54-2.813.26-5.162-1.166-6.814a5.575 5.575 0 00-1.592-1.249z"
            key="key-3"
        />
        <path
            fill="#222D65"
            d="M25.934 8.668c-.471-.139-.95-.247-1.434-.324a17.929 17.929 0 00-2.892-.214h-8.765c-.334 0-.656.12-.91.341-.253.22-.42.526-.472.861L9.597 21.34l-.054.35c.058-.382.249-.73.538-.982.289-.251.657-.39 1.037-.39h3.28c6.444 0 11.49-2.66 12.964-10.357.044-.228.081-.45.115-.667a7.786 7.786 0 00-1.213-.52c-.11-.037-.22-.072-.33-.105z"
            key="key-4"
        />
        <path
            fill="#253B80"
            d="M11.461 9.332c.051-.335.219-.64.472-.861a1.38 1.38 0 01.91-.34h8.765c1.038 0 2.007.069 2.892.214.598.096 1.189.24 1.765.428.436.147.84.32 1.213.52.438-2.845-.004-4.781-1.517-6.535C24.294.827 21.283 0 17.431 0H6.25c-.787 0-1.458.582-1.58 1.373L.013 31.392a.993.993 0 00.218.786.963.963 0 00.73.343h6.903L9.597 21.34 11.46 9.332z"
            key="key-5"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Paypal"
});
